/**
 * AI Mock Data Generators
 * Provides functions for generating realistic mock data for AI testing scenarios
 */
import { AINotificationType } from '../types/ai';
import { ContentType } from '../types/journey';
/**
 * Generate mock AI user profile
 */
export function generateMockAIUserProfile(userId, skillLevel = 'intermediate') {
    const baseDate = new Date();
    return {
        userId,
        skillAssessment: generateMockSkillAssessment(userId, skillLevel),
        learningPreferences: generateMockLearningPreferences(skillLevel),
        behaviorPatterns: generateMockBehaviorPatterns(userId),
        masteryStates: generateMockMasteryStates(userId, skillLevel),
        adaptationHistory: generateMockAdaptationHistory(userId),
        recommendationFeedback: generateMockRecommendationFeedback(userId),
        lastUpdated: baseDate.toISOString(),
        version: 1,
    };
}
/**
 * Generate mock skill assessment
 */
export function generateMockSkillAssessment(userId, skillLevel) {
    return {
        id: `assessment_${userId}_${Date.now()}`,
        userId,
        assessmentType: 'initial',
        skillLevel: skillLevel.toUpperCase(), // Will be properly typed when SkillLevel enum is imported
        experienceAreas: generateExperienceAreas(skillLevel),
        preferredMethods: generatePreferredMethods(skillLevel),
        availableEquipment: generateAvailableEquipment(skillLevel),
        timeAvailability: skillLevel === 'beginner' ? 15 : skillLevel === 'intermediate' ? 30 : 45,
        learningGoals: generateLearningGoals(skillLevel),
        confidence: skillLevel === 'beginner'
            ? 0.3
            : skillLevel === 'intermediate'
                ? 0.7
                : 0.9,
        completedAt: new Date().toISOString(),
        results: generateAssessmentResults(),
    };
}
/**
 * Generate mock mastery states
 */
export function generateMockMasteryStates(userId, skillLevel) {
    const skills = [
        { id: 'brewing_basics', name: 'Brewing Basics', category: 'Fundamentals' },
        { id: 'grind_size', name: 'Grind Size Control', category: 'Fundamentals' },
        { id: 'water_temp', name: 'Water Temperature', category: 'Fundamentals' },
        { id: 'pour_over', name: 'Pour Over Technique', category: 'Methods' },
        { id: 'espresso', name: 'Espresso Extraction', category: 'Methods' },
        { id: 'milk_steaming', name: 'Milk Steaming', category: 'Advanced' },
        { id: 'latte_art', name: 'Latte Art', category: 'Advanced' },
        { id: 'cupping', name: 'Coffee Cupping', category: 'Tasting' },
    ];
    const masteryMultiplier = {
        beginner: 0.3,
        intermediate: 0.6,
        advanced: 0.85,
    };
    return skills.map(skill => ({
        id: `mastery_${userId}_${skill.id}`,
        userId,
        skillId: skill.id,
        skillCategory: skill.category,
        skillName: skill.name,
        masteryLevel: Math.min(1, masteryMultiplier[skillLevel] + (Math.random() * 0.3 - 0.15)),
        confidence: 0.7 + Math.random() * 0.3,
        trend: ['improving', 'stable', 'declining'][Math.floor(Math.random() * 3)],
        evidenceCount: Math.floor(Math.random() * 20) + 5,
        lastUpdated: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        aiInsights: generateSkillInsights(skill.name, skillLevel),
        nextMilestone: generateNextMilestone(skill.name, skillLevel),
    }));
}
/**
 * Generate mock content recommendations
 */
export function generateMockContentRecommendations(userId, count = 5) {
    const contentTypes = [
        ContentType.RECIPE,
        ContentType.VIDEO,
        ContentType.READING,
        ContentType.PRACTICE,
    ];
    const recommendations = [];
    for (let i = 0; i < count; i++) {
        const contentType = contentTypes[Math.floor(Math.random() * contentTypes.length)];
        recommendations.push({
            id: `rec_${userId}_${Date.now()}_${i}`,
            userId,
            contentId: `content_${Math.random().toString(36).substr(2, 9)}`,
            contentType,
            title: generateContentTitle(contentType),
            description: generateContentDescription(contentType),
            thumbnailUrl: `https://example.com/thumbnails/${Math.random().toString(36).substr(2, 9)}.jpg`,
            estimatedMinutes: Math.floor(Math.random() * 30) + 5,
            difficultyLevel: Math.random(),
            skillAlignment: generateMockSkillAlignments(),
            confidence: 0.6 + Math.random() * 0.4,
            reasoning: generateMockRecommendationExplanation(),
            priority: Math.random(),
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            createdAt: new Date().toISOString(),
            metadata: {
                source: 'ai_recommendation_engine',
                algorithm: 'collaborative_filtering_v2',
            },
        });
    }
    return recommendations;
}
/**
 * Generate mock smart notifications
 */
export function generateMockSmartNotifications(userId, count = 3) {
    const notificationTypes = Object.values(AINotificationType);
    const notifications = [];
    for (let i = 0; i < count; i++) {
        const type = notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
        notifications.push({
            id: `notif_${userId}_${Date.now()}_${i}`,
            userId,
            type,
            title: generateNotificationTitle(type),
            content: generateNotificationContent(type),
            aiReasoning: generateNotificationReasoning(type),
            optimalTiming: new Date(Date.now() + Math.random() * 24 * 60 * 60 * 1000).toISOString(),
            confidence: 0.5 + Math.random() * 0.5,
            personalizationFactors: generatePersonalizationFactors(),
            priority: ['high', 'medium', 'low'][Math.floor(Math.random() * 3)],
            channel: ['push', 'email', 'in_app'][Math.floor(Math.random() * 3)],
            scheduledFor: new Date(Date.now() + Math.random() * 60 * 60 * 1000).toISOString(),
            expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
            metadata: {
                campaign: 'ai_engagement_v1',
                segment: 'active_learners',
            },
        });
    }
    return notifications;
}
/**
 * Generate mock dashboard insights
 */
export function generateMockDashboardInsights(userId) {
    return {
        nextBestAction: {
            id: `action_${userId}_${Date.now()}`,
            type: 'try_recipe',
            title: 'Perfect Your Pour Over',
            description: 'Try this medium-roast Ethiopian recipe to practice your pouring technique',
            estimatedMinutes: 15,
            confidence: 0.85,
            reasoning: 'Based on your recent progress in pour over fundamentals and preference for Ethiopian beans',
            priority: 'high',
            contentId: 'recipe_ethiopian_pour_over_v2',
        },
        progressInsights: generateMockProgressInsights(userId),
        motivationalMessage: generateMotivationalMessage(),
        adaptationSuggestions: generateMockAdaptationSuggestions(userId),
        streakInfo: {
            current: Math.floor(Math.random() * 30) + 1,
            longest: Math.floor(Math.random() * 60) + 10,
            target: 30,
            daysUntilBreak: Math.floor(Math.random() * 3),
            encouragement: "You're on fire! Keep up the great work.",
        },
        upcomingMilestones: generateUpcomingMilestones(),
        personalizedGreeting: generatePersonalizedGreeting(),
    };
}
/**
 * Generate mock behavior patterns
 */
function generateMockBehaviorPatterns(userId) {
    return [
        {
            id: `pattern_${userId}_engagement`,
            userId,
            patternType: 'engagement',
            pattern: {
                peakHours: [7, 8, 19, 20],
                averageSessionLength: 25,
                preferredDays: ['monday', 'wednesday', 'saturday'],
            },
            confidence: 0.8,
            frequency: 0.7,
            lastObserved: new Date().toISOString(),
            impact: 'high',
        },
        {
            id: `pattern_${userId}_learning`,
            userId,
            patternType: 'learning',
            pattern: {
                preferredContentLength: 'medium',
                skipRate: 0.15,
                completionRate: 0.85,
                retryFrequency: 0.3,
            },
            confidence: 0.75,
            frequency: 0.9,
            lastObserved: new Date().toISOString(),
            impact: 'medium',
        },
    ];
}
/**
 * Generate experience areas based on skill level
 */
function generateExperienceAreas(_skillLevel) {
    const areas = {
        beginner: ['home_brewing', 'coffee_shop_visits'],
        intermediate: [
            'home_brewing',
            'coffee_shop_visits',
            'different_methods',
            'bean_varieties',
        ],
        advanced: [
            'home_brewing',
            'coffee_shop_visits',
            'different_methods',
            'bean_varieties',
            'roasting',
            'cupping',
        ],
    };
    return areas[_skillLevel] || areas.intermediate;
}
/**
 * Generate preferred methods based on skill level
 */
function generatePreferredMethods(_skillLevel) {
    const methods = {
        beginner: ['drip_coffee', 'french_press'],
        intermediate: ['drip_coffee', 'french_press', 'pour_over', 'aeropress'],
        advanced: [
            'pour_over',
            'espresso',
            'chemex',
            'v60',
            'aeropress',
            'cold_brew',
        ],
    };
    return methods[_skillLevel] || methods.intermediate;
}
/**
 * Generate available equipment based on skill level
 */
function generateAvailableEquipment(_skillLevel) {
    const equipment = {
        beginner: ['basic_grinder', 'drip_machine', 'french_press'],
        intermediate: ['burr_grinder', 'pour_over_setup', 'scale', 'thermometer'],
        advanced: [
            'professional_grinder',
            'espresso_machine',
            'multiple_brewers',
            'refractometer',
        ],
    };
    return (equipment[_skillLevel] || equipment.intermediate);
}
/**
 * Generate learning goals based on skill level
 */
function generateLearningGoals(_skillLevel) {
    const goals = {
        beginner: ['make_better_coffee', 'understand_basics', 'consistent_results'],
        intermediate: ['master_pour_over', 'explore_origins', 'improve_technique'],
        advanced: ['perfect_espresso', 'develop_palate', 'teach_others', 'compete'],
    };
    return goals[_skillLevel] || goals.intermediate;
}
/**
 * Generate assessment results
 */
function generateAssessmentResults() {
    return [
        {
            questionId: 'q1_experience',
            questionType: 'multiple_choice',
            response: 'intermediate',
            confidence: 0.8,
            timeSpent: 15,
        },
        {
            questionId: 'q2_equipment',
            questionType: 'equipment',
            response: ['burr_grinder', 'pour_over_setup'],
            confidence: 0.9,
            timeSpent: 20,
        },
    ];
}
/**
 * Generate skill insights
 */
function generateSkillInsights(skillName, _skillLevel) {
    const insights = [
        `Your ${skillName.toLowerCase()} technique has improved significantly`,
        `Focus on consistency to master ${skillName.toLowerCase()}`,
        `Consider advanced ${skillName.toLowerCase()} techniques`,
    ];
    return insights.slice(0, Math.floor(Math.random() * 3) + 1);
}
/**
 * Generate next milestone
 */
function generateNextMilestone(skillName, skillLevel) {
    const milestones = {
        beginner: `Complete 5 ${skillName.toLowerCase()} exercises`,
        intermediate: `Achieve 80% consistency in ${skillName.toLowerCase()}`,
        advanced: `Master advanced ${skillName.toLowerCase()} techniques`,
    };
    return (milestones[skillLevel] || milestones.intermediate);
}
/**
 * Generate content title based on type
 */
function generateContentTitle(contentType) {
    const titles = {
        [ContentType.RECIPE]: [
            'Perfect Pour Over Recipe',
            'Ethiopian Single Origin',
            'Morning Blend Espresso',
        ],
        [ContentType.VIDEO]: [
            'Pour Over Technique Masterclass',
            'Latte Art Basics',
            'Coffee Cupping Guide',
        ],
        [ContentType.READING]: [
            'Understanding Coffee Origins',
            'Grind Size Guide',
            'Water Quality Impact',
        ],
        [ContentType.PRACTICE]: [
            'Consistency Challenge',
            'Timing Practice',
            'Taste Training',
        ],
        [ContentType.QUIZ]: [
            'Coffee Knowledge Quiz',
            'Brewing Method Quiz',
            'Origin Identification',
        ],
    };
    const titleList = titles[contentType] || titles[ContentType.RECIPE];
    return titleList[Math.floor(Math.random() * titleList.length)];
}
/**
 * Generate content description
 */
function generateContentDescription(contentType) {
    const descriptions = {
        [ContentType.RECIPE]: 'A carefully crafted recipe to help you practice and improve your brewing technique.',
        [ContentType.VIDEO]: 'Step-by-step video tutorial with expert tips and common mistake corrections.',
        [ContentType.READING]: 'In-depth article covering theory and practical applications for better coffee.',
        [ContentType.PRACTICE]: 'Interactive exercise designed to build muscle memory and consistency.',
        [ContentType.QUIZ]: 'Knowledge assessment to test your understanding and identify areas for improvement.',
    };
    return descriptions[contentType] || descriptions[ContentType.RECIPE];
}
/**
 * Generate mock skill alignments
 */
function generateMockSkillAlignments() {
    return [
        {
            skillId: 'pour_over',
            skillName: 'Pour Over Technique',
            alignmentScore: 0.85,
            gapFilled: true,
            masteryImpact: 0.15,
        },
        {
            skillId: 'grind_consistency',
            skillName: 'Grind Consistency',
            alignmentScore: 0.7,
            gapFilled: false,
            masteryImpact: 0.1,
        },
    ];
}
/**
 * Generate mock recommendation explanation
 */
function generateMockRecommendationExplanation() {
    return {
        primaryReason: 'This content matches your current skill level and learning preferences',
        confidence: 0.8,
        skillAlignment: generateMockSkillAlignments(),
        difficultyFit: 0.85,
        personalizedFactors: [
            'Recent activity in pour over techniques',
            'Preference for video content',
            'Morning learning schedule',
        ],
        alternativeOptions: ['French Press fundamentals', 'Espresso basics course'],
    };
}
// Additional helper functions for generating various mock data components...
function generateMockLearningPreferences(skillLevel) {
    return {
        focusAreas: ['brewing_technique', 'coffee_knowledge'],
        difficultyPreference: skillLevel.toUpperCase(),
        contentTypes: ['VIDEO', 'RECIPE', 'EXERCISE'],
        learningStyle: 'VISUAL',
        notificationPreferences: {
            enabled: true,
            frequency: 'DAILY',
            quietHours: { start: '22:00', end: '07:00' },
            types: ['REMINDERS', 'ACHIEVEMENTS'],
        },
        sessionDuration: 30,
        preferredTimes: ['morning', 'evening'],
        adaptationSensitivity: 0.7,
    };
}
function generateMockAdaptationHistory(userId) {
    return [
        {
            id: `adapt_${userId}_1`,
            userId,
            eventType: 'difficulty_adjustment',
            trigger: 'Low completion rate detected',
            oldValue: 0.8,
            newValue: 0.6,
            reasoning: 'Reduced difficulty to improve engagement',
            confidence: 0.75,
            userAccepted: true,
            timestamp: new Date().toISOString(),
            impact: {
                engagementChange: 0.2,
                performanceChange: 0.15,
                satisfactionChange: 0.1,
                retentionChange: 0.05,
            },
        },
    ];
}
function generateMockRecommendationFeedback(userId) {
    return [
        {
            id: `feedback_${userId}_1`,
            recommendationId: 'rec_123',
            userId,
            feedbackType: 'accepted',
            rating: 4,
            reason: 'Helpful content',
            timestamp: new Date().toISOString(),
            context: { source: 'dashboard' },
        },
    ];
}
function generateMockProgressInsights(userId) {
    return [
        {
            id: `insight_${userId}_1`,
            type: 'achievement',
            title: 'Consistency Improved',
            description: 'Your brewing consistency has improved by 25% this week',
            metric: 'consistency_score',
            value: 0.85,
            change: 0.25,
            trend: 'up',
            aiGenerated: true,
        },
    ];
}
function generateMotivationalMessage() {
    const messages = [
        "You're making excellent progress! Keep up the great work.",
        "Your dedication to learning is inspiring. Ready for today's challenge?",
        "Every cup is a step forward. Let's brew something amazing today!",
    ];
    return messages[Math.floor(Math.random() * messages.length)];
}
function generateMockAdaptationSuggestions(userId) {
    return [
        {
            id: `suggestion_${userId}_1`,
            type: 'difficulty',
            title: 'Increase Challenge Level',
            description: "You're ready for more advanced techniques",
            currentValue: 0.6,
            suggestedValue: 0.8,
            reasoning: 'High completion rate and positive feedback indicate readiness',
            confidence: 0.8,
            impact: {
                engagementChange: 0.1,
                performanceChange: 0.15,
                satisfactionChange: 0.05,
                retentionChange: 0.02,
            },
            urgency: 'medium',
        },
    ];
}
function generateUpcomingMilestones() {
    return [
        {
            id: 'milestone_1',
            title: 'Pour Over Master',
            description: 'Complete 10 pour over recipes with 80% consistency',
            progress: 7,
            target: 10,
            estimatedCompletion: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            reward: 'Advanced Techniques Unlock',
        },
    ];
}
function generatePersonalizedGreeting() {
    const greetings = [
        'Good morning, coffee enthusiast!',
        'Ready to brew something amazing?',
        'Welcome back to your coffee journey!',
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
}
function generateNotificationTitle(type) {
    const titles = {
        [AINotificationType.ACTIVITY_REMINDER]: 'Time for your coffee practice!',
        [AINotificationType.STREAK_REMINDER]: 'Keep your streak alive!',
        [AINotificationType.MILESTONE_ACHIEVEMENT]: 'Milestone achieved!',
        [AINotificationType.NEW_RECOMMENDATION]: 'New content just for you',
        [AINotificationType.SKILL_MASTERY]: 'Skill mastery unlocked!',
        [AINotificationType.JOURNEY_ADAPTATION]: 'Your journey has been updated',
        [AINotificationType.SOCIAL_ACTIVITY]: 'Your coffee community is active',
        [AINotificationType.CONTENT_SUGGESTION]: 'Perfect content match found',
        [AINotificationType.RE_ENGAGEMENT]: 'We miss you!',
        [AINotificationType.CELEBRATION]: 'Time to celebrate!',
    };
    return titles[type] || 'Coffee update';
}
function generateNotificationContent(type) {
    const content = {
        [AINotificationType.ACTIVITY_REMINDER]: 'Your optimal learning time is now. Ready for a 15-minute session?',
        [AINotificationType.STREAK_REMINDER]: "You're 1 day away from your longest streak. Don't break it now!",
        [AINotificationType.MILESTONE_ACHIEVEMENT]: "Congratulations! You've mastered pour over basics.",
        [AINotificationType.NEW_RECOMMENDATION]: 'We found a perfect Ethiopian recipe for your skill level.',
        [AINotificationType.SKILL_MASTERY]: "You've achieved 80% mastery in grind consistency!",
        [AINotificationType.JOURNEY_ADAPTATION]: "Based on your progress, we've added advanced techniques to your journey.",
        [AINotificationType.SOCIAL_ACTIVITY]: "Sarah just completed the same challenge you're working on!",
        [AINotificationType.CONTENT_SUGGESTION]: 'This latte art tutorial is perfect for your current skill level.',
        [AINotificationType.RE_ENGAGEMENT]: 'Your coffee journey is waiting. Come back and continue learning!',
        [AINotificationType.CELEBRATION]: "You've completed 30 days of consistent practice. Amazing work!",
    };
    return content[type] || "Check out what's new in your coffee journey.";
}
function generateNotificationReasoning(_type) {
    return `AI selected this notification based on your learning patterns, current progress, and optimal engagement times.`;
}
function generatePersonalizationFactors() {
    return [
        'Morning learning preference',
        'High engagement with video content',
        'Recent pour over activity',
        'Intermediate skill level',
    ];
}
